import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sentinelActions } from 'src/redux/sentinel/actions';
import { createSentinelDataByIdSelector } from 'src/redux/sentinel/selectors';
import { DqSocketContextData, DqSocketRequestOptions, InferSocketResponse, SentinelKey } from './types';
import { useSocket } from './useSocket';

export function useSocketGet<K extends SentinelKey, R extends InferSocketResponse<K>>(
  Context: React.Context<DqSocketContextData>,
  tag: K,
  id?: string,
  version?: string,
) {
  const dispatch = useDispatch();
  const selector = React.useMemo(() => createSentinelDataByIdSelector(tag, id), [id, tag]);
  const item = useSelector(selector);
  const [loadingKey, setLoadingKey] = React.useState<number | undefined>(undefined);

  const created = React.useCallback(
    (data: R) => {
      if (data?._id === id) {
        dispatch(sentinelActions.upsertOne<K>({ key: tag, value: data }));
      }
    },
    [dispatch, id, tag],
  );

  const updated = React.useCallback(
    (data: Partial<R>, set: any) => {
      if (data?._id === id) {
        try {
          const updated: Partial<R> = {};
          Object.keys(set).forEach(key => {
            const keys = key.split('.');
            updated[keys[0] as keyof R] = data[keys[0] as keyof R];
          });
          dispatch(sentinelActions.updateOnePartial({ key: tag, value: updated, id: data._id! }));
        } catch (error) {
          console.warn('set entire item', error);
          dispatch(sentinelActions.upsertOne({ key: tag, value: data as R })); // FIXME: Possibly unsafe, what actually gets sent as `data`?
        }
      }
    },
    [dispatch, id, tag],
  );

  const deleted = React.useCallback(
    (data: any) => {
      if (data?._id === id && !!id) {
        dispatch(sentinelActions.removeOne({ key: tag, id }));
      }
    },
    [dispatch, id, tag],
  );

  const { request, connected } = useSocket(Context, tag, created, updated, deleted);

  const get = React.useCallback(
    async (id: string, options?: DqSocketRequestOptions) => {
      const item = await request<R>(`${tag} get${version ? version : ''}`, { _id: id }, options);
      dispatch(sentinelActions.upsertOne({ key: tag, value: item }));
      return item;
    },
    [dispatch, request, tag, version],
  );

  React.useEffect(() => {
    const abort = new AbortController();

    const exec = async () => {
      const key = Date.now();
      setLoadingKey(key);
      try {
        if (!connected || !id) return;
        await get(id, { signal: abort.signal });
      } catch {
      } finally {
        if (abort.signal.aborted) return;

        setLoadingKey(current => {
          if (current === key) return undefined;
          return current;
        });
      }
    };
    exec().catch(console.warn);

    return () => {
      abort.abort();
    };
  }, [connected, get, id, tag]);

  return { connected, item, fetched: !!item, isLoading: loadingKey !== undefined };
}
